import {
  type ComputedRef,
  type InjectionKey,
  computed,
  inject,
  provide,
} from 'vue';

/**
 * 浮动面板 overlay 上下文。
 */
export interface FloatingPanelOverlayContext {
  /** 浮动面板 z-index 基底。 */
  zIndex: ComputedRef<number>;
}

/**
 * 浮动面板 overlay 上下文注入键。
 */
export const floatingPanelOverlayContextKey: InjectionKey<FloatingPanelOverlayContext> = Symbol(
  'floatingPanelOverlayContext',
);

/**
 * 下拉/弹出层相对浮动面板的 z-index 增量。
 *
 * 设计说明：Naive UI Popover/Dropdown 默认 z-index 低于 FloatingPanel（4000），
 * 在浮动面板内使用时需在其基底之上渲染。
 */
export const FLOATING_PANEL_OVERLAY_Z_INDEX_OFFSET = 1;

/**
 * 向子树提供浮动面板 overlay z-index 上下文。
 *
 * @param zIndex 浮动面板 z-index。
 */
export function provideFloatingPanelOverlayContext(zIndex: ComputedRef<number>): void {
  provide(floatingPanelOverlayContextKey, { zIndex });
}

/**
 * 解析浮动面板内弹出层应使用的 z-index。
 *
 * @param offset 相对浮动面板的 z-index 增量。
 * @returns 弹出层 z-index；不在浮动面板内时为 undefined，沿用 Naive UI 默认值。
 */
export function useFloatingPanelOverlayZIndex(
  offset: number = FLOATING_PANEL_OVERLAY_Z_INDEX_OFFSET,
): ComputedRef<number | undefined> {
  const context = inject(floatingPanelOverlayContextKey, null);

  return computed(() => {
    if (!context) {
      return undefined;
    }
    return context.zIndex.value + offset;
  });
}
